import { useRef, useState, useEffect } from "react";
import Tooltip from "@mui/material/Tooltip";
import { decryptObject } from "./encrypt";
import dayjs from "dayjs";

export const getCurrencySymbol = (currencyCode = "INR") => {
  switch (currencyCode?.toUpperCase()) {
    case "INR":
      return "₹";
    case "USD":
      return "$";
    case "EUR":
      return "€";
    case "GBP":
      return "£";
    case "JPY":
      return "¥";
    case "CNY":
      return "¥";
    case "AUD":
      return "A$";
    case "CAD":
      return "C$";
    case "AED":
      return "د.إ";
    case "CHF":
      return "CHF";
    case "ZAR":
      return "R";
    case "SGD":
      return "S$";
    case "BTC":
      return "₿";
    default:
      return currencyCode || "";
  }
};

export const AutoTooltip = ({ children }) => {
  const spanRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    if (!spanRef.current) return;

    const isOverflowing =
      spanRef.current.scrollWidth > spanRef.current.clientWidth;

    setShowTooltip(isOverflowing);
  }, [children]);

  return (
    <Tooltip
      title={showTooltip ? children : ""}
      arrow
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            fontSize: "14px",
            padding: "6px 10px",
            borderRadius: "8px",
            backdropFilter: "blur(6px)",
            background: "rgba(50,50,50,0.85)",
            color: "white",
          },
        },
      }}
    >
      <span
        ref={spanRef}
        style={{
          display: "block", //  IMPORTANT
          width: "100%", //  IMPORTANT
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          cursor: "pointer",
          verticalAlign: "middle",
        }}
      >
        {children}
      </span>
    </Tooltip>
  );
};

export const convertUTCtoDubai = (utcString) => {
  if (!utcString) return "NA";

  try {
    const date = new Date(utcString);

    const options = {
      timeZone: "Asia/Dubai",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    };

    const parts = new Intl.DateTimeFormat("en-US", options).formatToParts(date);

    const day = parts.find((p) => p.type === "day").value;
    const month = parts.find((p) => p.type === "month").value;
    const year = parts.find((p) => p.type === "year").value;
    const hour = parts.find((p) => p.type === "hour").value;
    const minute = parts.find((p) => p.type === "minute").value;
    const second = parts.find((p) => p.type === "second").value;
    const dayPeriod = parts.find((p) => p.type === "dayPeriod").value;

    return `${day} ${month} ${year} ${hour}:${minute}:${second} ${dayPeriod}`;
  } catch (err) {
    return "NA";
  }
};

export const parseValue = (val) => {
  if (!val) return 0;
  return parseFloat(String(val).replace(/[^\d.-]/g, ""));
};

export const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return decodeURIComponent(parts.pop().split(";").shift());
  }
  return null;
};


export function getDecodedUserData() {
  const userData = getCookie("userData");
  if (!userData || typeof userData !== "string") {
    return null;
  }

  let decryptedUserData;
  try {
    decryptedUserData = decryptObject(userData);
    return decryptedUserData;
  } catch (err) {
    console.error("Token decryption failed:", err);
    return null;
  }
}

export const htmlTagRegex = /<[^>]*>/i; // blocks <script>, <div>, etc.
export const linkRegex = /(https?:\/\/|www\.|mailto:)/i;

const hasUnsafeContent = (value) =>
  typeof value === "string" &&
  (htmlTagRegex.test(value) || linkRegex.test(value));

export const isValidUsername = (value) => {
  if (!value) return false;
  if (hasUnsafeContent(value)) return false;

  return /^[A-Za-z]+$/.test(value);
};

export const isValidEmail = (value) => {
  if (!value) return false;
  if (hasUnsafeContent(value)) return false;

  const atCount = (value.match(/@/g) || []).length;
  if (atCount !== 1) return false;

  if (value.includes("..")) return false;

  const emailRegex =
    /^[a-zA-Z0-9]+([._-][a-zA-Z0-9]+)*@[a-zA-Z0-9]+([.-][a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/;

  return emailRegex.test(value);
};


export const isValidPhone = (value) => {
  if (!value) return false;
  if (hasUnsafeContent(value)) return false;

  return /^(?:\+971|971|\+97|97|0)?5[0-9]{8}$/.test(value);
};

export const isValidPassword = (value) => {
  if (!value) return false;
  if (hasUnsafeContent(value)) return false;

  return /^(?=.*[0-9])(?=.*[!@#$%^&*])[A-Za-z0-9!@#$%^&*]{8,}$/.test(value);
};

export const allowOnlyAlphanumericAndSpace = (value) => {
  if (hasUnsafeContent(value)) return false;

  const regex = /^[a-zA-Z0-9][a-zA-Z0-9 -]{0,34}$/;
  return regex.test(value);
};

export const allowOnlyAlphanumeric = (value) => {
  if (hasUnsafeContent(value)) return false;

  const regex = /^[a-zA-Z0-9]*$/;
  return regex.test(value);
};
export const allowVehicleText = (value) => {
  if (!value || typeof value !== "string") return false;
  if (hasUnsafeContent(value)) return false;

  const regex = /^[A-Za-z0-9 .,&()'+\-\/]{1,40}$/;
  return regex.test(value.trim());
};

export const isValidDriverId = (value) => {
  if (hasUnsafeContent(value)) return false;

  const regex = /^[0-9]{0,30}$/;
  return regex.test(value);
};

export const allowDubaiAddress = (value) => {
  if (!value || !value.trim()) return false;

  const regex = /^[a-zA-Z0-9\u0600-\u06FF ,\/\-–\.\#()'’:&+]+$/;
  return regex.test(value.trim());
};
/* =========================
   RFID VALIDATIONS
========================= */

export const validateRFID = (value) => {
  if (!value || typeof value !== "string") {
    return "RFID is required";
  }

  if (hasUnsafeContent(value)) {
    return "Invalid characters detected in RFID";
  }

  if (!/^[A-Z0-9]+$/.test(value)) {
    return "RFID must contain only uppercase letters and numbers";
  }

  if (value.length < 8) {
    return "RFID must be at least 8 characters long";
  }

  if (value.length > 16) {
    return "RFID cannot exceed 16 characters";
  }

  return ""; //   valid
};

export const validateExpiryDate = (value) => {
  if (!value) {
    return "Expiry date is required";
  }

  const selectedDate = dayjs(value);
  const today = dayjs().startOf("day");

  if (!selectedDate.isValid()) {
    return "Invalid expiry date";
  }

  if (selectedDate.isBefore(today)) {
    return "Expiry date must be in the future";
  }

  return "";
};

